import {
  Button,
  Column,
  Container,
  Copy,
  Flex,
  Heading,
  Row,
  Section,
  Stack,
  SvgPrinter,
} from '@grubhubprod/gh-cookbook-react/dist';
import React from 'react';
import PrintContentContainer from 'src/components/Print/PrintContentContainer';

import { OrderType } from 'src/components/enterprise/features/OrderHistory/OrderReceipt/OrderType';
import {
  FulfillmentInfo,
  IFulfillmentInfoProps,
} from 'src/components/enterprise/features/OrderHistory/OrderReceipt/FulfillmentInfo';
import { SidePanel } from 'src/components/enterprise/features/OrderHistory/OrderReceipt/SidePanel';
import { LineItems } from 'src/common/Order/OrderReceipt/LineItems';
import {
  Charges,
  IChargeProps,
} from 'src/components/enterprise/features/OrderHistory/OrderReceipt/Charges';
import { AllocationDetails } from 'src/components/enterprise/features/OrderHistory/OrderReceipt/AllocationDetails';
import { ReceiptLine } from 'src/components/enterprise/features/OrderHistory/OrderReceipt/ReceiptLine';
import { HeadingAndValues } from 'src/components/enterprise/features/OrderHistory/OrderReceipt/HeadingAndValues';
import { BackToOrderHistory } from 'src/components/enterprise/features/OrderHistory/OrderReceipt/BackToOrderHistory';
import PageSection from 'src/components/enterprise/layout/PageSection';
import PrintContent from 'src/components/Print/PrintContent';
import { IItem, IParticipantLineMap } from 'src/types/OrderHistory';
import { GroupOrderLines } from 'src/common/Order/OrderReceipt/GroupOrderLines';
import LogoImage from 'src/components/enterprise/navigation/Logo/LogoImage';

export interface IAllocationProps {
  name: string;
  amount: string;
}

export interface ICorpDataProps {
  id: string;
  name: string;
}

export interface IOrderReceiptProps {
  dinerName: string;
  email: string;
  corpData: ICorpDataProps;
  timePlaced: string;
  expenseCode?: string;
  expenseComments?: string;
  fulfillmentInfo: IFulfillmentInfoProps;
  orderType: string;
  orderNumber: string;
  restaurantName: string;
  items: IItem[];
  charges: IChargeProps[];
  allocations: IAllocationProps[];
  transactionFee: string;
  total: string;
  isGroupOrder: boolean;
  isGroupAdmin: boolean;
  participantLines?: IParticipantLineMap[];
}

export function OrderReceipt({
  dinerName,
  email,
  corpData: { id: corpId, name: corpName },
  timePlaced,
  fulfillmentInfo,
  expenseCode,
  expenseComments,
  orderType,
  orderNumber,
  restaurantName,
  items,
  charges,
  allocations,
  transactionFee,
  total,
  isGroupOrder,
  isGroupAdmin,
  participantLines,
}: IOrderReceiptProps): JSX.Element {
  const receiptContent = 'receipt-content';

  return (
    <Container variant="default">
      <PrintContentContainer id={receiptContent}>
        <Section inset="md">
          <Stack y="lg" className="print_hidden">
            <Flex alignItems="center" justifyContent="space-between">
              <BackToOrderHistory corpId={corpId} />
              <PrintContent
                containerId={receiptContent}
                title={`Order number: ${orderNumber}`}
              >
                <Flex alignItems="center">
                  <Stack as="span">
                    <Copy variant="body" color="interactive">
                      <SvgPrinter size="24px" />
                    </Copy>
                  </Stack>

                  <Button variant="tertiary" buttonSize="medium">
                    Print receipt
                  </Button>
                </Flex>
              </PrintContent>
            </Flex>
          </Stack>
          <Stack
            y="md"
            className="print_visibleForPrint"
            borderBottomWidth="1px"
            borderBottomStyle="solid"
            borderBottomColor="lightLine"
          >
            <Stack y="xxx-sm">
              <LogoImage width="84px" />
            </Stack>
          </Stack>
          <Row>
            <Column
              desktop={4}
              tablet={4}
              phone={4}
              data-testid="order-descriptive-data-section"
            >
              <SidePanel>
                <HeadingAndValues
                  heading="Ordered by"
                  values={[dinerName, email, corpName, timePlaced]}
                />
                <FulfillmentInfo {...fulfillmentInfo} />
                <HeadingAndValues
                  heading="Order number"
                  values={[orderNumber]}
                />
                {expenseCode && (
                  <HeadingAndValues
                    heading="Expense code"
                    values={[expenseCode]}
                  />
                )}
                {expenseComments && (
                  <HeadingAndValues
                    heading="Expense comments"
                    values={[expenseComments]}
                  />
                )}
              </SidePanel>
            </Column>

            <Column
              desktop={8}
              tablet={8}
              phone={8}
              data-testid="order-financial-data-section"
            >
              <Stack y="md">
                <PageSection>
                  <OrderType
                    isGroupOrder={isGroupOrder}
                    orderType={orderType}
                  />
                  <Heading variant="h2">{restaurantName}</Heading>
                </PageSection>
                <PageSection>
                  {isGroupOrder && isGroupAdmin ? (
                    <GroupOrderLines
                      hostLine={{
                        dinerName,
                        isHost: true,
                        items,
                      }}
                      participantLines={participantLines}
                    />
                  ) : (
                    <LineItems items={items} />
                  )}
                </PageSection>
                <PageSection>
                  <Charges value={charges} />
                </PageSection>
              </Stack>
              <PageSection>
                <Stack y="sm">
                  <Heading variant="h5">Your receipt</Heading>
                </Stack>
                <AllocationDetails value={allocations} />
                <ReceiptLine name="Transaction fee" value={transactionFee} />
                <ReceiptLine name="Total" value={total} />
              </PageSection>
            </Column>
          </Row>
        </Section>
      </PrintContentContainer>
    </Container>
  );
}
